import React from 'react';
import { CalendarIcon, CheckCircleIcon, LinkIcon } from '@heroicons/react/24/outline';

const CalendarSettings = ({ 
  settings, 
  onInputChange, 
  loading,
  onConnect,
  onDisconnect,
  onTest,
  onSaveCalendarSettings
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Calendar Integration
        </h3>

        <div
          className={`p-6 rounded-lg border-2 ${
            settings.calendar_connected
              ? "border-green-200 bg-green-50"
              : "border-gray-200 bg-white"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div
                className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                  settings.calendar_connected ? "bg-green-100" : "bg-gray-100"
                }`}
              >
                {settings.calendar_connected ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                ) : (
                  <CalendarIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <div>
                <h4
                  className={`font-medium ${
                    settings.calendar_connected
                      ? "text-green-900"
                      : "text-gray-900"
                  }`}
                >
                  {settings.calendar_connected
                    ? "Calendar Connected"
                    : "Calendar Not Connected"}
                </h4>
                <p
                  className={`text-sm ${
                    settings.calendar_connected
                      ? "text-green-700"
                      : "text-gray-500"
                  }`}
                >
                  {settings.calendar_connected
                    ? `Connected to ${
                        settings.calendar_email ||
                        settings.calendar_provider ||
                        "your calendar"
                      }`
                    : "Connect your calendar to enable automatic appointment booking"}
                </p>
                {settings.calendar_connected && settings.calendar_name && (
                  <p className="text-xs text-green-600 mt-1">
                    Calendar: {settings.calendar_name}
                  </p>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              {settings.calendar_connected ? (
                <>
                  <button
                    onClick={onTest}
                    disabled={loading}
                    className="px-3 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 text-sm"
                  >
                    Test
                  </button>
                  <button
                    onClick={onDisconnect}
                    disabled={loading}
                    className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    Disconnect
                  </button>
                </>
              ) : (
                <button
                  onClick={onConnect}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <LinkIcon className="h-4 w-4" />
                  <span>Connect Calendar</span>
                </button>
              )}
            </div>
          </div>

          {settings.calendar_connected && (
            <div className="text-sm text-green-700">
              <p>✅ Your AI assistant can now automatically book appointments</p>
              <p>✅ Customers can see your real availability</p>
              <p>✅ No double bookings or scheduling conflicts</p>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Booking Availability</h4>
            {settings.calendar_connected && (
              <button
                onClick={onSaveCalendarSettings}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                Save Settings
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Time
              </label>
              <input
                type="time"
                value={settings.start_time}
                onChange={(e) => onInputChange("start_time", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.calendar_connected}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Time
              </label>
              <input
                type="time"
                value={settings.end_time}
                onChange={(e) => onInputChange("end_time", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.calendar_connected}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buffer Time (minutes)
              </label>
              <select
                value={settings.buffer_minutes}
                onChange={(e) =>
                  onInputChange("buffer_minutes", parseInt(e.target.value))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.calendar_connected}
              >
                <option value={0}>No buffer</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Duration (minutes)
              </label>
              <select
                value={settings.default_duration}
                onChange={(e) =>
                  onInputChange("default_duration", parseInt(e.target.value))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.calendar_connected}
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Days
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  "monday",
                  "tuesday",
                  "wednesday",
                  "thursday",
                  "friday",
                  "saturday",
                  "sunday",
                ].map((day) => (
                  <label key={day} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.working_days?.includes(day)}
                      onChange={(e) => {
                        const updatedDays = e.target.checked
                          ? [...(settings.working_days || []), day]
                          : (settings.working_days || []).filter((d) => d !== day);
                        onInputChange("working_days", updatedDays);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={!settings.calendar_connected}
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">
                      {day}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timezone
              </label>
              <select
                value={settings.timezone || "America/New_York"}
                onChange={(e) => onInputChange("timezone", e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!settings.calendar_connected}
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="America/Phoenix">Arizona Time</option>
                <option value="America/Anchorage">Alaska Time</option>
                <option value="Pacific/Honolulu">Hawaii Time</option>
              </select>
            </div>
          </div>

          {!settings.calendar_connected && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600">
                Connect your calendar above to configure booking availability settings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(CalendarSettings);
